import { create } from 'zustand';
import type { JobIdentity, JobIdentityState } from './types';
import { STATUS_COLORS } from '@/lib/statusColors';

// Step 9: default RGB color when no status is known
const DEFAULT_COLOR: [number, number, number] = [120, 130, 140];

function statusToColor(status?: string): [number, number, number] {
  if (!status) return DEFAULT_COLOR;
  const hex = STATUS_COLORS[status];
  if (!hex) return DEFAULT_COLOR;
  // Parse hex string to RGB tuple (hex format: '#rrggbb')
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return DEFAULT_COLOR;
  return [r, g, b];
}

export const useJobIdentity = create<JobIdentityState>()((set, get) => ({
  identities: new Map(),

  setJobs: (rows: any[]) => {
    const identities = new Map<string, JobIdentity>();
    for (const row of rows) {
      // Support rows that come from both the table (job_id) and map (katapult_id, firebase_id)
      const id = String(row.id ?? row.job_id ?? row.katapult_id ?? '');
      const katapultId = String(row.katapult_id ?? row.katapultId ?? id);
      const jobName = String(row.name ?? row.job_name ?? row.jobName ?? '');
      const firebaseId = row.firebase_id ?? row.firebaseId ?? undefined;
      // Step 9: capture status and derive statusColor at index-time so callers
      // never need to import statusColors or compute colors on the fly.
      const status = row.status ?? row.status_label ?? undefined;
      const statusColor = statusToColor(status);

      const identity: JobIdentity = { id, katapultId, jobName, firebaseId, status, statusColor };

      // Index by all known IDs so any can resolve to the canonical identity
      if (id) identities.set(id, identity);
      if (katapultId && katapultId !== id) identities.set(katapultId, identity);
      if (firebaseId && firebaseId !== id && firebaseId !== katapultId) {
        identities.set(String(firebaseId), identity);
      }
      // Also index by job name so ESRI map clicks (which return the feature's 'jobname'
      // attribute) can be resolved back to the canonical UUID identity.
      if (jobName && jobName !== '—' && jobName !== id && jobName !== katapultId) {
        identities.set(jobName, identity);
      }
    }
    set({ identities });
  },

  resolve: (unknownId: string): JobIdentity | undefined => {
    return get().identities.get(String(unknownId));
  },

  resolveToMapId: (id: string, engine: 'deepsync' | 'esri'): string | undefined => {
    const identity = get().identities.get(String(id));
    if (!identity) return undefined;
    if (engine === 'esri') {
      // ArcGIS feature layers key on the Katapult UUID in their 'jobid' / 'job_id' field.
      // This is an external system identifier, so we use the external katapultId.
      return identity.katapultId;
    }
    // DeepSync (Deck.gl) uses the canonical internal job_id — the business key.
    // Per architecture charter: business logic IDs are used internally; external
    // system IDs (katapultId) are only used at integration boundaries (ESRI).
    return identity.id;
  },

  resolveFromMapId: (mapId: string, _engine: 'deepsync' | 'esri'): string | undefined => {
    const identity = get().identities.get(String(mapId));
    return identity?.id;
  },

  resolveMany: (ids: string[], engine: 'deepsync' | 'esri'): string[] => {
    const { resolveToMapId } = get();
    return ids.flatMap((id) => {
      const mapId = resolveToMapId(id, engine);
      return mapId ? [mapId] : [];
    });
  },

  // Step 9: Build a per-job color map from stored status metadata.
  // Used by fitToJobs to send color-coded highlights to both map engines
  // without any component needing to import or re-derive statusColors.
  getJobColors: (ids: string[]): Record<string, [number, number, number]> => {
    const { identities } = get();
    const colors: Record<string, [number, number, number]> = {};
    for (const id of ids) {
      const identity = identities.get(String(id));
      colors[id] = identity?.statusColor ?? DEFAULT_COLOR;
    }
    return colors;
  },
}));
